import React, { useState, useRef, useEffect } from 'react';
import { colors2 } from 'oolib';
import { FloatingPortal, FloatingArrow } from './hooks/useFloatingPosition';
import { useFloatingPosition } from './hooks/useFloatingPosition';
import {
    autoSafeFlip,
    ARROW_PADDING,
    renderContent,
    resolvePrimaryContent,
    getBorderRadius,
} from './utils/index';
import {
    StyledTooltipWrapper,
    StyledTooltipContent,
    StyledInlineWrapper,
} from './styled';
import { TooltipV2Props, TooltipPropsWrapper } from './types';
import ControlledTooltip from './comps/ControlledTooltip';


function WrapperTooltip({
    text,
    heading,
    primaryContent,
    secondaryContent,
    progressiveDelay = 600,
    progressiveGap = 8,
    children,
    placement = 'top',
    showPointer = true,
    showDelay = 600,
    hideDelay = 0,
    animation = 'fade',
    invert = false,
    wrapperDisplay,
    trigger = 'hover',
    style,
    wrapperStyle,
    open,
    onOpenChange,
}: TooltipPropsWrapper) {
    const bgColor = invert ? colors2.white : colors2.black;
    const textColor = invert ? colors2.black : colors2.white;
    const resolvedPrimaryContent = resolvePrimaryContent(text, heading, primaryContent);

    // Controlled-open support: when `open` is passed, visibility is driven
    // externally; otherwise fall back to internal state (default behavior).
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlledOpen = open !== undefined;
    const isOpen = isControlledOpen ? open : internalOpen;
    const setIsOpen = (next: boolean) => {
        if (!isControlledOpen) setInternalOpen(next);
        onOpenChange?.(next);
    };
    const [showSecondary, setShowSecondary] = useState(false);
    const [isPinned, setIsPinned] = useState(false);

    const progressiveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const arrowRef = useRef(null);

    // Progressive tooltip logic
    useEffect(() => {
        if (isOpen && secondaryContent) {
            progressiveTimerRef.current = setTimeout(() => setShowSecondary(true), progressiveDelay);
        } else {
            setShowSecondary(false);
            if (progressiveTimerRef.current) clearTimeout(progressiveTimerRef.current);
        }
        return () => {
            if (progressiveTimerRef.current) clearTimeout(progressiveTimerRef.current);
        };
    }, [isOpen, progressiveDelay, secondaryContent]);

    const hasFadeAnimation = animation === 'fade';
    const transitionOptions = hasFadeAnimation ? {
        duration: 150,
        initial: { opacity: 0 },
        open: { opacity: 1 },
        close: { opacity: 0 },
    } : undefined;

    const isClickTrigger = trigger === 'click';

    const {
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        isMounted,
        transitionStyles,
    } = useFloatingPosition({
        open: isOpen,
        onOpenChange: (open) => {
            if (isClickTrigger) {
                setIsOpen(open);
                return;
            }
            if (isPinned) {
                if (!open) {
                    setIsPinned(false);
                    setIsOpen(false);
                }
                return;
            }
            setIsOpen(open);
        },
        placement,
        showArrow: showPointer,
        arrowRef,
        arrowPadding: ARROW_PADDING,
        customMiddleware: [autoSafeFlip],
        enableDelayGroups: !isClickTrigger,
        enableTriggerOnClick: isClickTrigger,
        enableHover: true,
        hoverOptions: {
            delay: { open: showDelay, close: hideDelay },
        },
        focusOptions: { enabled: !isClickTrigger },
        dismissOptions: { enabled: true },
        roleOptions: { role: 'tooltip' },
        transitionOptions,
        middlewareOptions: {
            offset: { config: 10 },
            shift: { enabled: true, config: { padding: 8 } },
            flip: { enabled: true, config: { fallbackAxisSideDirection: 'start' } },
        },
    });

    const handleClick = (e: React.MouseEvent) => {
        if (isClickTrigger) return;
        e.stopPropagation();
        if (isOpen) {
            setIsPinned(false);
            setIsOpen(false);
        } else {
            setIsPinned(true);
            setIsOpen(true);
        }
    };

    const isVertical = placement.startsWith('top') || placement.startsWith('bottom');
    const secondaryFirst = placement.startsWith('top') || placement.startsWith('left');

    return (
        <>
            <StyledInlineWrapper
                ref={refs.setReference}
                {...getReferenceProps({ onClick: handleClick })}
                $display={wrapperDisplay}
                style={wrapperStyle}
            >
                {children}
            </StyledInlineWrapper>
            {(hasFadeAnimation ? isMounted : isOpen) && (
                <FloatingPortal>
                    <StyledTooltipWrapper
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            ...(hasFadeAnimation ? transitionStyles : {}),
                            ...style,
                        }}
                        $isVertical={isVertical}
                        $gap={progressiveGap}
                        {...getFloatingProps()}
                    >
                        <StyledTooltipContent
                            $bgColor={bgColor}
                            $textColor={textColor}
                            $borderRadius={getBorderRadius({
                                isPrimary: true,
                                progressiveGap,
                                secondaryContent: !!secondaryContent,
                                showSecondary,
                                placement,
                            })}
                            $order={1}
                        >
                            {resolvedPrimaryContent && renderContent(resolvedPrimaryContent)}
                            {showPointer && <FloatingArrow ref={arrowRef} context={context} fill={bgColor} tipRadius={2} />}
                        </StyledTooltipContent>
                        {secondaryContent && showSecondary && (
                            <StyledTooltipContent
                                $bgColor={bgColor}
                                $textColor={textColor}
                                $borderRadius={getBorderRadius({
                                    isPrimary: false,
                                    progressiveGap,
                                    secondaryContent: !!secondaryContent,
                                    showSecondary,
                                    placement,
                                })}
                                $order={secondaryFirst ? 0 : 2}
                            >
                                {renderContent(secondaryContent)}
                            </StyledTooltipContent>
                        )}
                    </StyledTooltipWrapper>
                </FloatingPortal>
            )}
        </>
    );
}

export default function TooltipV2(props: TooltipV2Props) {

    if (props.controlled) {
        return <ControlledTooltip {...props} />;
    }

    return <WrapperTooltip {...(props as TooltipPropsWrapper)} />;
}

export type {
    TooltipV2Props,
    ContentConfig,
    TooltipPropsWrapper,
    TooltipPropsControlled,
} from './types';
